import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  FaArrowLeft, FaArrowRight, FaCalendarAlt, FaCrown, FaCheckCircle,
  FaWeight, FaBullseye, FaSmile, FaChartLine, FaDumbbell, FaUtensils,
  FaExclamationTriangle, FaEnvelope, FaRocket
} from 'react-icons/fa';
import { toast } from 'sonner';
import { filterForSpecialPrice } from '@/lib/renewalPeriods';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RenewalPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [renewalStatus, setRenewalStatus] = useState(null);
  const [showRenewalPrompt, setShowRenewalPrompt] = useState(true);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    previous_weight: '',
    current_weight: '',
    goal_changed: false,
    new_goal: '',
    plan_feeling: '',
    plan_compliance: '',
    training_days_changed: false,
    new_training_days: '',
    problematic_exercises: '',
    foods_to_include: '',
    foods_to_exclude: '',
    additional_notes: ''
  });

  useEffect(() => {
    fetchRenewalStatus();
  }, []);

  const fetchRenewalStatus = async () => {
    let userId = null;
    // 1. Intentar obtener el user_id (no es bloqueante para el resto)
    try {
      const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: false });
      userId = userResponse.data?.id;
    } catch (e) {
      console.error('fetchRenewalStatus: auth/me failed', e);
    }
    // Si no tenemos userId, intentamos del localStorage (fallback)
    if (!userId) {
      try {
        const stored = JSON.parse(localStorage.getItem('deisycfit_user') || '{}');
        userId = stored?.id;
      } catch (e) { /* noop */ }
    }
    if (!userId) {
      setLoading(false);
      // Sin user_id, no podemos seguir, pero no mostramos toast feo. Dejamos la UI sin estado.
      console.warn('fetchRenewalStatus: no se pudo determinar user_id, saltando llamada de status');
      return;
    }
    // 2. Cargar el estado de renovación. Si falla, mostramos error suave (sólo log)
    try {
      const response = await axios.get(`${API}/renewal/status/${userId}`, { withCredentials: false });
      if (response?.data) {
        setRenewalStatus(response.data);
        if (response.data.can_submit_update_form) {
          setShowRenewalPrompt(true);
        }
      }
    } catch (error) {
      console.error('Error fetching renewal status:', error);
      // Sólo mostramos el toast si NO hay datos previos en pantalla.
      // Evita el caso en que el endpoint respondió en una llamada y falla en otra (StrictMode).
      if (!renewalStatus) {
        toast.error('No pudimos cargar el estado de renovación. Refresca la página.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProceedToForm = () => {
    setShowRenewalPrompt(false);
    setShowUpdateForm(true);
  };

  // 'yearly' se guarda como 'annual' dentro de pricing_options por compatibilidad
  // con el shape previo del backend; el resto de periodos usan la misma clave.
  const PERIOD_TO_OPTS_KEY = {
    monthly: 'monthly', bimonthly: 'bimonthly', quarterly: 'quarterly',
    semiannual: 'semiannual', yearly: 'annual',
  };

  const handleSelectBilling = (periodKey) => {
    const opts = renewalStatus?.pricing_options;
    if (!opts) return;
    const slug = opts.plan_slug;
    const period = opts[PERIOD_TO_OPTS_KEY[periodKey] || periodKey];
    const amount = period?.cop || period?.usd;
    const params = new URLSearchParams({
      plan: slug,
      period: periodKey,
      amount: String(amount || ''),
      renewal: '1',
    });
    navigate(`/checkout?${params.toString()}`);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let userId = null;
      try {
        const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: false });
        userId = userResponse.data?.id;
      } catch (e) { /* fallback abajo */ }
      if (!userId) {
        try {
          const stored = JSON.parse(localStorage.getItem('deisycfit_user') || '{}');
          userId = stored?.id;
        } catch (e) { /* noop */ }
      }
      if (!userId) {
        toast.error('Sesión no encontrada. Por favor vuelve a entrar.');
        setSubmitting(false);
        return;
      }

      const submitData = {
        ...formData,
        previous_weight: parseFloat(formData.previous_weight) || 0,
        current_weight: parseFloat(formData.current_weight) || 0,
        new_training_days: formData.training_days_changed ? parseInt(formData.new_training_days) : null
      };

      await axios.post(`${API}/renewal/submit-update/${userId}`, submitData, {
        withCredentials: false
      });

      setShowUpdateForm(false);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error submitting update:', error);
      toast.error(error?.response?.data?.detail || 'Error al enviar el formulario. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.previous_weight || !formData.current_weight)) {
      toast.error('Por favor ingresa tu peso anterior y actual');
      return;
    }
    if (step === 2 && !formData.plan_feeling) {
      toast.error('Por favor indica cómo te has sentido con el plan');
      return;
    }
    if (step === 3 && !formData.plan_compliance) {
      toast.error('Por favor indica qué porcentaje del plan has cumplido');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  // Pantalla de confirmación final
  if (showConfirmation) {
    return (
      <div className="min-h-screen py-12 px-6" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl shadow-xl p-8 text-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
              <FaCheckCircle className="text-4xl text-green-400" />
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-4">
              ¡Formulario de Actualización Enviado! 🚀
            </h1>
            
            <p className="text-lg text-gray-300 mb-6">
              ¡Excelente decisión seguir avanzando! Estamos preparando tu nuevo plan para el 
              <strong className="text-[var(--brand-primary)]"> Mes {(renewalStatus?.current_month || 0) + 1}</strong>.
            </p>

            <div className="rounded-xl p-6 mb-6 text-left" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.1)' }}>
              <h3 className="font-bold text-[var(--brand-primary)] text-lg mb-3 flex items-center gap-2">
                <FaRocket className="text-[var(--brand-primary)]" />
                ¿Qué sigue?
              </h3>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[var(--brand-primary)]">•</span>
                  <span>Tu entrenadora revisará tu progreso y preparará un plan más avanzado</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[var(--brand-primary)]">•</span>
                  <span>Recibirás tu nuevo plan en un plazo de <strong>24-48 horas</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[var(--brand-primary)]">•</span>
                  <span>Te notificaremos por email cuando esté listo</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', border: '2px solid rgba(245, 158, 11, 0.3)' }}>
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="text-amber-400 text-xl flex-shrink-0 mt-1" />
                <p className="text-amber-300 text-sm text-left">
                  <strong>¡Importante!</strong> Revisa tu carpeta de spam y agrega 
                  <strong> deisycpbusiness@gmail.com</strong> a tus contactos para no perderte la notificación.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/my-workout')}
                className="text-white font-bold rounded-full"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
              >
                Ver Mi Rutina Actual
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 rounded-full"
              >
                Ir al Dashboard
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Prompt de renovación inicial
  if (showRenewalPrompt) {
    return (
      <div className="min-h-screen py-12 px-6" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-2xl mx-auto">
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-6 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl shadow-xl overflow-hidden"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {/* Header con gradiente */}
            <div className="p-8 text-center text-white" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
                <FaCrown className="text-3xl" />
              </div>
              <h1 className="text-3xl font-bold mb-2">¿Qué tal te ha parecido todo hasta ahora?</h1>
              <p className="text-white/90">Mes {renewalStatus?.current_month || 1} completado</p>
            </div>

            <div className="p-8">
              {/* Estado actual */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="rounded-xl p-4 text-center" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.15)' }}>
                  <FaCalendarAlt className="text-2xl text-[var(--brand-primary)] mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Mes Actual</p>
                  <p className="text-2xl font-bold text-[var(--brand-primary)]">{renewalStatus?.current_month || 1}</p>
                </div>
                <div className="rounded-xl p-4 text-center" style={{ backgroundColor: 'rgba(0, 240, 255, 0.15)' }}>
                  <FaChartLine className="text-2xl text-[#00F0FF] mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Días restantes</p>
                  <p className="text-2xl font-bold text-[#00F0FF]">{renewalStatus?.days_until_expiry || 0}</p>
                </div>
              </div>

              {/* Mensaje motivacional */}
              <div className="rounded-xl p-6 mb-8" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.1)' }}>
                <p className="text-gray-300 leading-relaxed">
                  Si deseas <strong className="text-white">seguir avanzando</strong> y llevar tu transformación al siguiente nivel, 
                  puedes renovar y continuar con tu proceso. Cada mes subiremos la intensidad, 
                  las técnicas y el volumen de entrenamiento para que sigas progresando.
                </p>
                <p className="text-gray-400 mt-4 italic">
                  Recuerda que estoy acá para ayudarte en lo que necesites. ¡Gracias por tu confianza! 💪
                </p>
                <p className="text-right text-[var(--brand-primary)] font-semibold mt-2">- Deisy Carolina</p>
              </div>

              {/* Periodos de renovación disponibles — se leen directo de pricing_options
                  en vez de mostrar siempre Mensual/Anual: cada plan puede tener menos
                  o más periodos configurados (bimestral, trimestral, semestral, anual). */}
              {renewalStatus?.pricing_options && (() => {
                const opts = renewalStatus.pricing_options;
                const PERIOD_META = [
                  { key: 'monthly', optsKey: 'monthly', title: 'Mensual', subtitle: 'Se renueva cada mes', testid: 'renewal-plan-monthly', accent: 'from-slate-700 to-slate-800' },
                  { key: 'bimonthly', optsKey: 'bimonthly', title: 'Bimestral', subtitle: 'Se renueva cada 2 meses', testid: 'renewal-plan-bimonthly', accent: 'from-slate-700 to-slate-800' },
                  { key: 'quarterly', optsKey: 'quarterly', title: 'Trimestral', subtitle: 'Se renueva cada 3 meses', testid: 'renewal-plan-quarterly', accent: 'from-slate-700 to-slate-800' },
                  { key: 'semiannual', optsKey: 'semiannual', title: 'Semestral', subtitle: 'Se renueva cada 6 meses', testid: 'renewal-plan-semiannual', accent: 'from-slate-700 to-slate-800' },
                  { key: 'yearly', optsKey: 'annual', title: 'Anual', subtitle: 'Un solo pago — 12 meses', testid: 'renewal-plan-annual', accent: 'from-pink-500 to-fuchsia-600', recommended: true },
                ];
                const priced = PERIOD_META.filter((m) => {
                  const o = opts[m.optsKey];
                  return o && (Number(o.cop) > 0 || Number(o.usd) > 0);
                });
                // Ticket #274906: cliente con precio especial (is_special) ve ese periodo + el anual.
                const available = filterForSpecialPrice(priced, opts);
                if (available.length === 0) return null;

                // Fórmula: ((mensual * 12) - anual) / (mensual * 12) * 100, redondeado.
                // Usamos COP como base; caemos a USD si no hay COP.
                const monthlyBase = Number(opts.monthly?.cop) || Number(opts.monthly?.usd) || 0;
                const annualBase = Number(opts.annual?.cop) || Number(opts.annual?.usd) || 0;
                const base = monthlyBase * 12;
                const savingsPct = base > 0 && annualBase > 0
                  ? Math.max(0, Math.round(((base - annualBase) / base) * 100))
                  : 0;

                const gridColsClass = {
                  1: '', 2: 'md:grid-cols-2', 3: 'sm:grid-cols-3',
                  4: 'sm:grid-cols-2 lg:grid-cols-4', 5: 'sm:grid-cols-2 lg:grid-cols-5',
                }[available.length] || 'md:grid-cols-2';

                return (
                <div className="mb-8">
                  <h3 className="text-center text-lg font-bold text-white mb-1">
                    Elige tu plan de renovación
                  </h3>
                  {savingsPct > 0 && (
                    <p className="text-center text-sm text-gray-400 mb-5" data-testid="renewal-savings-caption">
                      Ahorra {savingsPct}% pagando por año — sin recargos, sin sorpresas
                    </p>
                  )}
                  <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
                    {available.map((meta) => {
                      const o = opts[meta.optsKey];
                      const isAnnual = meta.optsKey === 'annual';
                      return (
                        <BillingOption
                          key={meta.key}
                          title={meta.title}
                          subtitle={meta.subtitle}
                          priceCop={o.cop}
                          priceUsd={o.usd}
                          strikeCop={isAnnual ? o.cop_full : undefined}
                          strikeUsd={isAnnual ? o.usd_full : undefined}
                          badge={isAnnual && savingsPct > 0 ? `AHORRA ${savingsPct}%` : null}
                          accent={meta.accent}
                          testid={meta.testid}
                          savingsCop={isAnnual ? o.savings_cop : undefined}
                          savingsUsd={isAnnual ? o.savings_usd : undefined}
                          onSelect={() => handleSelectBilling(meta.key)}
                          recommended={!!meta.recommended}
                        />
                      );
                    })}
                  </div>
                </div>
                );
              })()}

              {/* Botón de renovación */}
              <div className="space-y-4">
                <Button 
                  onClick={handleProceedToForm}
                  className="w-full py-6 text-lg text-white font-bold rounded-full"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                  data-testid="renewal-proceed-form-btn"
                >
                  <FaRocket className="mr-2" />
                  ¡Quiero Seguir Avanzando!
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => navigate('/plans')}
                  className="w-full border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 rounded-full"
                >
                  Ver todos los planes
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Formulario de actualización
  if (showUpdateForm) {
    return (
      <div className="min-h-screen py-12 px-6" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-3xl mx-auto">
          <Button variant="outline" onClick={() => setShowRenewalPrompt(true)} className="mb-6 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white mb-4" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>
              <FaRocket className="text-2xl" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              ¡Excelente decisión! 🎉
            </h1>
            <p className="text-gray-400">
              Vamos al siguiente nivel. Necesito algunos datos para preparar tu nuevo plan.
            </p>
            
            {/* Progress indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className="h-2 w-12 rounded-full transition-all"
                  style={{ backgroundColor: s <= step ? 'var(--brand-primary)' : 'rgba(255,255,255,0.2)' }}
                />
              ))}
            </div>
          </motion.div>

          <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardHeader>
              <CardTitle className="text-white">Paso {step} de 4</CardTitle>
              <CardDescription className="text-gray-400">
                {step === 1 && "Control de peso"}
                {step === 2 && "¿Cómo te has sentido?"}
                {step === 3 && "Cumplimiento del plan"}
                {step === 4 && "Ajustes para el próximo mes"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              <AnimatePresence mode="wait">
                {/* Step 1: Peso */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          <FaWeight className="inline mr-2 text-[var(--brand-primary)]" />
                          Peso al iniciar (kg)
                        </label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="Ej: 65.5"
                          value={formData.previous_weight}
                          onChange={(e) => handleChange('previous_weight', e.target.value)}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          <FaWeight className="inline mr-2 text-[#00F0FF]" />
                          Peso actual (kg)
                        </label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="Ej: 63.0"
                          value={formData.current_weight}
                          onChange={(e) => handleChange('current_weight', e.target.value)}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        />
                      </div>
                    </div>

                    {formData.previous_weight && formData.current_weight && (
                      <div className="p-4 rounded-xl text-center" style={{
                        backgroundColor: parseFloat(formData.current_weight) < parseFloat(formData.previous_weight)
                          ? 'rgba(34, 197, 94, 0.15)'
                          : parseFloat(formData.current_weight) > parseFloat(formData.previous_weight)
                          ? 'rgba(59, 130, 246, 0.15)'
                          : 'rgba(255,255,255,0.05)',
                        color: parseFloat(formData.current_weight) < parseFloat(formData.previous_weight)
                          ? '#4ade80'
                          : parseFloat(formData.current_weight) > parseFloat(formData.previous_weight)
                          ? '#60a5fa'
                          : '#9ca3af'
                      }}>
                        <p className="font-semibold">
                          {parseFloat(formData.current_weight) < parseFloat(formData.previous_weight)
                            ? `¡Bajaste ${(parseFloat(formData.previous_weight) - parseFloat(formData.current_weight)).toFixed(1)} kg! 🎉`
                            : parseFloat(formData.current_weight) > parseFloat(formData.previous_weight)
                            ? `Subiste ${(parseFloat(formData.current_weight) - parseFloat(formData.previous_weight)).toFixed(1)} kg 💪`
                            : 'Tu peso se mantiene igual'}
                        </p>
                      </div>
                    )}

                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-300">
                        <FaBullseye className="inline mr-2 text-[var(--brand-primary)]" />
                        ¿Quieres cambiar tu objetivo?
                      </label>
                      <div className="flex gap-4">
                        <Button
                          type="button"
                          variant={!formData.goal_changed ? "default" : "outline"}
                          onClick={() => handleChange('goal_changed', false)}
                          className={!formData.goal_changed ? "text-white" : "border-[var(--brand-primary)] text-[var(--brand-primary)]"}
                          style={!formData.goal_changed ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
                        >
                          Mantener objetivo
                        </Button>
                        <Button
                          type="button"
                          variant={formData.goal_changed ? "default" : "outline"}
                          onClick={() => handleChange('goal_changed', true)}
                          className={formData.goal_changed ? "bg-purple-600" : ""}
                        >
                          Cambiar objetivo
                        </Button>
                      </div>

                      {formData.goal_changed && (
                        <div className="grid grid-cols-2 gap-3 mt-4">
                          {[
                            { value: 'perder_grasa', label: 'Perder Grasa', icon: '🔥' },
                            { value: 'ganar_musculo', label: 'Ganar Músculo', icon: '💪' },
                            { value: 'mantener', label: 'Mantener', icon: '⚖️' },
                            { value: 'definicion', label: 'Definición', icon: '✨' }
                          ].map((goal) => (
                            <div
                              key={goal.value}
                              className={`p-4 border-2 rounded-xl cursor-pointer transition-all text-center ${
                                formData.new_goal === goal.value
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 hover:border-purple-300'
                              }`}
                              onClick={() => handleChange('new_goal', goal.value)}
                            >
                              <span className="text-2xl">{goal.icon}</span>
                              <p className="font-medium mt-1">{goal.label}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Sentimiento */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        <FaSmile className="inline mr-2 text-purple-500" />
                        ¿Cómo te has sentido con el plan?
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { value: 'excelente', label: 'Excelente', emoji: '🤩', color: 'green' },
                          { value: 'bien', label: 'Bien', emoji: '😊', color: 'blue' },
                          { value: 'regular', label: 'Regular', emoji: '😐', color: 'yellow' },
                          { value: 'mal', label: 'Mal', emoji: '😞', color: 'red' }
                        ].map((feeling) => (
                          <div
                            key={feeling.value}
                            className={`p-6 border-2 rounded-xl cursor-pointer transition-all text-center ${
                              formData.plan_feeling === feeling.value
                                ? `border-${feeling.color}-500 bg-${feeling.color}-50`
                                : 'border-gray-200 hover:border-purple-300'
                            }`}
                            onClick={() => handleChange('plan_feeling', feeling.value)}
                          >
                            <span className="text-4xl">{feeling.emoji}</span>
                            <p className="font-medium mt-2">{feeling.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Cumplimiento */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        <FaChartLine className="inline mr-2 text-purple-500" />
                        ¿Qué porcentaje del plan pudiste cumplir?
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { value: '100%', label: '100%', desc: 'Cumplí todo', color: 'bg-green-500' },
                          { value: '75%', label: '75%', desc: 'Casi todo', color: 'bg-blue-500' },
                          { value: '50%', label: '50%', desc: 'La mitad', color: 'bg-yellow-500' },
                          { value: 'menos_50%', label: 'Menos del 50%', desc: 'Poco', color: 'bg-red-500' }
                        ].map((compliance) => (
                          <div
                            key={compliance.value}
                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                              formData.plan_compliance === compliance.value
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300'
                            }`}
                            onClick={() => handleChange('plan_compliance', compliance.value)}
                          >
                            <div className={`w-full h-2 rounded-full bg-gray-200 mb-3`}>
                              <div 
                                className={`h-full rounded-full ${compliance.color}`}
                                style={{ width: compliance.value === 'menos_50%' ? '30%' : compliance.value }}
                              />
                            </div>
                            <p className="font-bold text-lg">{compliance.label}</p>
                            <p className="text-sm text-gray-600">{compliance.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Ajustes */}
                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    {/* Días de entrenamiento */}
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700">
                        <FaDumbbell className="inline mr-2 text-purple-500" />
                        ¿Cambian tus días de entrenamiento?
                      </label>
                      <div className="flex gap-4">
                        <Button
                          type="button"
                          variant={!formData.training_days_changed ? "default" : "outline"}
                          onClick={() => handleChange('training_days_changed', false)}
                          className={!formData.training_days_changed ? "bg-purple-600" : ""}
                        >
                          Mantener días
                        </Button>
                        <Button
                          type="button"
                          variant={formData.training_days_changed ? "default" : "outline"}
                          onClick={() => handleChange('training_days_changed', true)}
                          className={formData.training_days_changed ? "bg-purple-600" : ""}
                        >
                          Cambiar días
                        </Button>
                      </div>

                      {formData.training_days_changed && (
                        <div className="grid grid-cols-4 gap-2 mt-4">
                          {[3, 4, 5, 6].map((days) => (
                            <div
                              key={days}
                              className={`p-4 border-2 rounded-xl cursor-pointer transition-all text-center ${
                                formData.new_training_days === days.toString()
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 hover:border-purple-300'
                              }`}
                              onClick={() => handleChange('new_training_days', days.toString())}
                            >
                              <p className="text-2xl font-bold text-purple-600">{days}</p>
                              <p className="text-xs text-gray-600">días</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Ejercicios problemáticos */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ¿Hay ejercicios que te causan molestias o lesiones?
                      </label>
                      <Textarea
                        placeholder="Ej: Me duele la rodilla al hacer sentadillas profundas..."
                        value={formData.problematic_exercises}
                        onChange={(e) => handleChange('problematic_exercises', e.target.value)}
                        rows={2}
                      />
                    </div>

                    {/* Alimentos */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FaUtensils className="inline mr-2 text-green-500" />
                          Alimentos a incluir
                        </label>
                        <Textarea
                          placeholder="Alimentos que quieres agregar..."
                          value={formData.foods_to_include}
                          onChange={(e) => handleChange('foods_to_include', e.target.value)}
                          rows={2}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FaUtensils className="inline mr-2 text-red-500" />
                          Alimentos a excluir
                        </label>
                        <Textarea
                          placeholder="Alimentos que no quieres..."
                          value={formData.foods_to_exclude}
                          onChange={(e) => handleChange('foods_to_exclude', e.target.value)}
                          rows={2}
                        />
                      </div>
                    </div>

                    {/* Notas adicionales */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notas adicionales (opcional)
                      </label>
                      <Textarea
                        placeholder="¿Algo más que quieras contarnos?"
                        value={formData.additional_notes}
                        onChange={(e) => handleChange('additional_notes', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navegación */}
              <div className="flex justify-between pt-6 border-t">
                {step > 1 ? (
                  <Button variant="outline" onClick={prevStep}>
                    <FaArrowLeft className="mr-2" /> Anterior
                  </Button>
                ) : (
                  <div />
                )}

                {step < 4 ? (
                  <Button onClick={nextStep} className="bg-purple-600 hover:bg-purple-700">
                    Siguiente <FaArrowRight className="ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <FaRocket className="mr-2" />
                        Enviar Actualización
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return null;
};

// ─────────────────────────────────────────────────────────
// BillingOption: tarjeta de opción de facturación (mensual/anual)
// ─────────────────────────────────────────────────────────
const BillingOption = ({
  title, subtitle, priceCop, priceUsd, strikeCop, strikeUsd,
  badge, accent, testid, savingsCop, savingsUsd, onSelect, recommended
}) => {
  const fmtCop = (n) => (n ? `$${Math.round(n).toLocaleString('es-CO')} COP` : '');
  const fmtUsd = (n) => (n ? `US$${Number(n).toLocaleString('en-US', { minimumFractionDigits: n < 10 ? 2 : 0, maximumFractionDigits: 2 })}` : '');
  return (
    <button
      onClick={onSelect}
      data-testid={testid}
      className={`relative text-left rounded-2xl p-5 transition-all bg-gradient-to-br ${accent} hover:brightness-110 ${recommended ? 'ring-2 ring-pink-300 shadow-[0_0_24px_rgba(236,72,153,0.35)]' : 'ring-1 ring-white/10'}`}
    >
      {badge && (
        <span className="absolute -top-3 right-4 rounded-full bg-yellow-400 text-black text-[10px] font-black px-3 py-1 tracking-wider">
          {badge}
        </span>
      )}
      <div className="text-white/80 text-xs uppercase tracking-widest">{title}</div>
      <div className="text-white text-sm mb-4">{subtitle}</div>

      <div className="flex items-baseline gap-2">
        <span className="text-white text-3xl font-black leading-none">{fmtCop(priceCop) || fmtUsd(priceUsd)}</span>
      </div>
      {(strikeCop || strikeUsd) && (
        <div className="mt-1 text-white/60 text-sm line-through">
          {strikeCop ? fmtCop(strikeCop) : fmtUsd(strikeUsd)}
        </div>
      )}
      {(savingsCop || savingsUsd) && (
        <div className="mt-3 inline-block rounded-full bg-white/15 text-white text-xs font-semibold px-3 py-1">
          Ahorras {savingsCop ? fmtCop(savingsCop) : fmtUsd(savingsUsd)} al año
        </div>
      )}
      <div className="mt-4 text-white/90 text-xs font-semibold">
        Elegir {title.toLowerCase()} →
      </div>
    </button>
  );
};

export default RenewalPage;
